/**
 * Development Authentication Bypass
 * Set REACT_APP_DEV_EMAIL and REACT_APP_DEV_PASSWORD in .env.local to auto-login locally
 * This creates a real Appwrite session so all API calls work properly.
 */

import { account } from './appwrite';

export function isDevModeEnabled() {
  // SECURITY: Only allow dev mode on localhost — never on deployed environments.
  // This is the primary safeguard. Even if credentials are accidentally baked
  // into a build, they will not be used on any non-localhost domain.
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (!isLocalhost) {
    return false;
  }

  // Treat missing or empty-string values as disabled. The deploy script and
  // .env.production both set these to '' so deployed bundles always return false.
  return !!(
    process.env.REACT_APP_DEV_EMAIL?.trim() &&
    process.env.REACT_APP_DEV_PASSWORD?.trim()
  );
}

/**
 * Create a real Appwrite session using dev credentials
 * This allows the app to function normally with proper permissions
 */
export async function createDevSession() {
  // SECURITY: Double-check we're on localhost before attempting dev auth
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    throw new Error('Dev authentication is only available on localhost');
  }

  const email = process.env.REACT_APP_DEV_EMAIL;
  const password = process.env.REACT_APP_DEV_PASSWORD;
  
  if (!email || !password) {
    throw new Error('Dev mode requires REACT_APP_DEV_EMAIL and REACT_APP_DEV_PASSWORD');
  }
  
  try {
    // Check if session already exists
    try {
      const existingSession = await account.getSession('current');
      if (existingSession) {
        console.log('[Dev Mode] Using existing session');
        return existingSession;
      }
    } catch {
      // No existing session, create one
    }
    
    // Create email+password session
    console.log('[Dev Mode] Creating session for:', email);
    const session = await account.createEmailPasswordSession(email, password);
    console.log('[Dev Mode] Session created successfully');
    return session;
  } catch (error) {
    console.error('[Dev Mode] Failed to create session:', error);
    throw new Error(`Dev authentication failed: ${error.message}`);
  }
}
